import {
  DeleteOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { unstable_usePrompt } from 'react-router-dom';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import List from '@/components/List/List';
import Typo from '@/components/Typography/Typo';
import queryKeys from '@/configs/react-query-keys';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore from '@/stores/pure/ToastStore';
import type { FilterItemsTextT } from '@/types/filterItems';

import FilterItemsAddText from './FilterItemsAddText';
import FilterItemText from './FilterItemText';
import FilterItemsTemplateDetectionModal from './Modal/FilterItemsTemplateDetectionModal';
import FilterItemsUploadTextModal from './Modal/FilterItemsUploadTextModal';
import FilterTextsProvider from './provider/FilterTextsProvider';
import { MODAL_ACTION, modalReducer,type ModalState } from './reducer/modalReducer';
import { DiffType, type EnhancedFilterItemTextT } from './type';

type FilterItemsTextProps = {
  setHasTextTabChange: Dispatch<SetStateAction<boolean>>;
  onSaveChange: (
    addedTexts: string[],
    deletedTexts: string[],
  ) => Promise<FilterItemsTextT>;
  editDisabled: boolean;
};

const initialModalState: ModalState = {
  isOpenUploadCSVModal: false,
  isOpenTemplateDetectionModal: false,
};

const FilterItemsText = (props: FilterItemsTextProps) => {
  const color = useColor();
  const { setPopup } = usePopupStore((state) => ({
    setPopup: state.setPopup,
  }));
  const [state, dispatch] = useReducer(modalReducer, initialModalState);
  const { data: filterItemsText, isLoading: isFilterItemsTextLoading } =
    useQuery({
      queryKey: queryKeys.filterItems.FILTER_ITEMS_ANALYSIS_TEXT,
      queryFn: async () => {
        const data = await service.filterItems.fetchAnalysisFilterTexts();
        setExistingTexts(data.texts || []);
        return data;
      },
    });
  const addDefaultToast = useToastStore((state) => state.addDefaultToast);

  const [existingTexts, setExistingTexts] = useState<string[]>([]);
  const [updatedTexts, setUpdatedTexts] = useState<EnhancedFilterItemTextT[]>(
    [],
  );

  useEffect(() => {
    props.setHasTextTabChange(updatedTexts.length > 0);
  }, [updatedTexts]);

  const toggleAIDetectionModal = () => {
    dispatch({ type: MODAL_ACTION.TOGGLE_TEMPLATE_DETECTION_MODAL });
  };

  const toggleUploadCSVModal = () => {
    dispatch({ type: MODAL_ACTION.TOGGLE_UPLOAD_CSV_MODAL });
  };

  const handleDeleteExistingText = (index: number) => {
    setExistingTexts((prev) => prev.filter((_, i) => i !== index));
    setUpdatedTexts((prev) => [
      ...prev,
      { type: DiffType.DELETED, text: existingTexts[index] },
    ]);
  };

  const handleDeleteUpdatedText = (index: number) => {
    const deletedText = updatedTexts[index];

    if (deletedText.type === DiffType.DELETED) {
      setExistingTexts((prev) => [...prev, deletedText.text]);
    }

    setUpdatedTexts((prev) => prev.filter((_, i) => i !== index));
  };

  const hasChange = updatedTexts.length > 0;

  const saveChange = async () => {
    if (!hasChange) {
      return;
    }

    const uniqueOldSet = [...new Set(filterItemsText?.texts)];

    const uniqueAddedSet = [
      ...new Set(
        updatedTexts
          .filter((item) => item.type === DiffType.ADDED)
          .map((item) => item.text),
      ),
    ];
    const uniqueDeletedSet = [
      ...new Set(
        updatedTexts
          .filter((item) => item.type === DiffType.DELETED)
          .map((item) => item.text),
      ),
    ];

    const addedTexts = uniqueAddedSet.filter(
      (text) => !uniqueOldSet.includes(text),
    );
    const deletedTexts = uniqueDeletedSet.filter((text) =>
      uniqueOldSet.includes(text),
    );

    try {
      const data = await props.onSaveChange(addedTexts, deletedTexts);

      setExistingTexts(data.texts || []);
      setUpdatedTexts([]);
    } catch (error) {
      addDefaultToast('Failed to save changes', 'fail');
      console.error(error);
    }
  };

  const handleClickSaveChange = async () => {
    if (!hasChange) {
      return;
    }

    if (props.editDisabled) {
      return;
    }

    setPopup({
      popupTheme: 'neutral',
      type: 'confirm',
      title: 'Are you sure you want to save the changes?',
      message:
        'It may take a few minutes to reflect the changes. Then, all your data will be re-analyzed using the list.',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: saveChange,
    });
  };

  const handleConfirmAddTexts = (texts: string[]) => {
    setUpdatedTexts((prev) => [
      ...prev,
      ...texts.map((text) => ({ type: DiffType.ADDED, text })),
    ]);
  };

  unstable_usePrompt({
    when: hasChange,
    message: 'Changes you made may not be saved.',
  });

  return (
    <FilterTextsProvider
      value={{
        existingTexts,
        setExistingTexts,
        updatedTexts,
        setUpdatedTexts,
      }}
    >
      <Space h={36} />

      <Flex j="space-between" a="center">
        <Typo type="label2" weight="sb" className="text-gray-800">
          Add text
        </Typo>

        <Flex g={8} a="center">
          <Button
            disabled={props.editDisabled}
            sizeType="m"
            variant="secondary"
            onClick={toggleAIDetectionModal}
            leftIcon={<AIIcon />}
          >
            AI Detection
          </Button>

          <Button
            disabled={props.editDisabled}
            sizeType="m"
            variant="secondary"
            onClick={toggleUploadCSVModal}
            leftIcon={<Plus />}
          >
            Upload CSV
          </Button>

          <Button
            disabled={props.editDisabled || !hasChange}
            sizeType="m"
            variant="primary"
            onClick={handleClickSaveChange}
          >
            Save updates
          </Button>
        </Flex>
      </Flex>

      <Space h={16} />

      <FilterItemsAddText />

      <Space h={40} />

      <Typo type="label2" weight="sb" className="text-gray-800">
        Pending List
      </Typo>
      <List
        data={updatedTexts}
        isLoading={isFilterItemsTextLoading}
        className="gap-2 py-6"
        render={(text, index) => {
          return (
            <Flex key={index} a="center" g={12}>
              {text.type === DiffType.ADDED ? (
                <PlusCircleOutlined
                  style={{ color: color.green600, fontSize: '16px' }}
                />
              ) : (
                <MinusCircleOutlined
                  style={{ color: color.red600, fontSize: '16px' }}
                />
              )}

              <FilterItemText
                readOnly
                key={index}
                text={text.text}
                actionButton={
                  !props.editDisabled && (
                    <IconButton
                      className="delete-button"
                      icon={
                        text.type === DiffType.ADDED ? (
                          <DeleteOutlined />
                        ) : (
                          <UndoOutlined />
                        )
                      }
                      outlined={false}
                      onClick={() => {
                        handleDeleteUpdatedText(index);
                      }}
                      sizeType="l"
                      style={{
                        flexShrink: 0,
                      }}
                    />
                  )
                }
              />
            </Flex>
          );
        }}
      />

      <Space h={16} />

      <Typo type="label2" weight="sb" className="text-gray-800">
        Existing List
      </Typo>
      <List
        data={existingTexts}
        isLoading={isFilterItemsTextLoading}
        className="gap-2 py-6"
        render={(text, index) => {
          return (
            <FilterItemText
              readOnly
              key={index}
              text={text}
              actionButton={
                !props.editDisabled && (
                  <IconButton
                    className="delete-button"
                    icon={<DeleteOutlined />}
                    outlined={false}
                    onClick={() => {
                      handleDeleteExistingText(index);
                    }}
                    sizeType="l"
                    style={{
                      flexShrink: 0,
                    }}
                  />
                )
              }
            />
          );
        }}
      />

      <FilterItemsTemplateDetectionModal
        isOpen={state.isOpenTemplateDetectionModal}
        closeModal={toggleAIDetectionModal}
        onConfirm={handleConfirmAddTexts}
        existingTexts={existingTexts.concat(
          updatedTexts.map((text) => text.text),
        )}
      />

      <FilterItemsUploadTextModal
        isOpen={state.isOpenUploadCSVModal}
        closeModal={toggleUploadCSVModal}
        onConfirm={handleConfirmAddTexts}
        existingTexts={existingTexts.concat(
          updatedTexts.map((text) => text.text),
        )}
      />
    </FilterTextsProvider>
  );
};

export default FilterItemsText;
